import PropTypes from "prop-types";

/**
 * High-visibility brand marks for channel filters.
 * Native brand shapes (rounded square / circle) — do not clip to a shared circle.
 */

function Svg({ children, size, title, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      
      aria-label={title}
      focusable="false"
      className={className}
      style={{ display: "block", flexShrink: 0 }}
    >
      <title>{title}</title>
      {children}
    </svg>
  );
}

Svg.propTypes = {
  children: PropTypes.node.isRequired,
  size: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
};

function ImageMark({ size, title, src, radius = 5, inset = 0, scale = 1 }) {
  const innerSize = Math.max(1, Math.round((size - (inset * 2)) * scale));
  return (
    <span
      aria-label={title}
      title={title}
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        borderRadius: radius,
        flexShrink: 0,
      }}
    >
      <img
        src={src}
        alt={title}
        width={innerSize}
        height={innerSize}
        style={{
          width: innerSize,
          height: innerSize,
          objectFit: scale > 1 ? "cover" : "contain",
          display: "block",
          flexShrink: 0,
        }}
      />
    </span>
  );
}

ImageMark.propTypes = {
  size: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  radius: PropTypes.number,
  inset: PropTypes.number,
  scale: PropTypes.number,
};

function LinkedInMark({ size }) {
  return <ImageMark size={size} title="LinkedIn" src="/brand/linkedin.png" radius={5} inset={0} />;
}

LinkedInMark.propTypes = { size: PropTypes.number.isRequired };

function QuoraMark({ size }) {
  return <ImageMark size={size} title="Quora" src="/brand/quora.png" radius={5} inset={0} />;
}

QuoraMark.propTypes = { size: PropTypes.number.isRequired };

function RedditMark({ size }) {
  return <ImageMark size={size} title="Reddit" src="/brand/reddit.png" radius={size / 2} inset={0} />;
}

RedditMark.propTypes = { size: PropTypes.number.isRequired };

function FacebookMark({ size }) {
  return <ImageMark size={size} title="Facebook" src="/brand/facebook.png" radius={size / 2} inset={0} />;
}

FacebookMark.propTypes = { size: PropTypes.number.isRequired };

function InstagramMark({ size }) {
  return <ImageMark size={size} title="Instagram" src="/brand/instagram.png" radius={6} inset={0} />;
}

InstagramMark.propTypes = { size: PropTypes.number.isRequired };

function GoogleAdsMark({ size }) {
  return <ImageMark size={size} title="Google Ads" src="/brand/google-ads.webp" radius={5} inset={0} />;
}

GoogleAdsMark.propTypes = { size: PropTypes.number.isRequired };

function ZillowMark({ size }) {
  return <ImageMark size={size} title="Zillow" src="/brand/zillow.png" radius={size / 2} scale={1.45} />;
}

ZillowMark.propTypes = { size: PropTypes.number.isRequired };

function RealtorMark({ size }) {
  return <ImageMark size={size} title="Realtor.com" src="/brand/realtor.png" radius={5} inset={0} />;
}

RealtorMark.propTypes = { size: PropTypes.number.isRequired };

function RedfinMark({ size }) {
  return <ImageMark size={size} title="Redfin" src="/brand/redfin.png" radius={size / 2} scale={1.45} />;
}

RedfinMark.propTypes = { size: PropTypes.number.isRequired };

function XMark({ size }) {
  return <ImageMark size={size} title="X" src="/brand/x.png" radius={size / 2} inset={0} />;
}

XMark.propTypes = { size: PropTypes.number.isRequired };

/** Website: indigo rounded square + globe. */
function WebsiteMark({ size }) {
  return (
    <Svg size={size} title="Website">
      <rect width="24" height="24" rx="5" fill="#4F46E5" />
      <circle cx="12" cy="12" r="6.4" fill="none" stroke="#FFFFFF" strokeWidth="1.65" />
      <ellipse cx="12" cy="12" rx="2.7" ry="6.4" fill="none" stroke="#FFFFFF" strokeWidth="1.65" />
      <path stroke="#FFFFFF" strokeWidth="1.65" fill="none" d="M5.6 12h12.8M6.9 8.6h10.2M6.9 15.4h10.2" />
    </Svg>
  );
}

WebsiteMark.propTypes = { size: PropTypes.number.isRequired };

function RncMark({ size }) {
  const fontSize = Math.max(8, Math.round(size * 0.36));
  return (
    <span
      aria-label="RNC"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.22),
        background: "#0F172A",
        color: "#fff",
        fontSize,
        fontWeight: 800,
        letterSpacing: "-0.03em",
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      RNC
    </span>
  );
}

RncMark.propTypes = { size: PropTypes.number.isRequired };

/** ABM List: teal rounded square + ABM label (matches RNC shape, keeps brand color). */
function AbmMark({ size }) {
  const fontSize = Math.max(7, Math.round(size * 0.34));
  const pad = Math.max(1, Math.round(size * 0.08));
  return (
    <span
      aria-label="ABM List"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        padding: pad,
        boxSizing: "border-box",
        borderRadius: Math.round(size * 0.22),
        background: "#0F766E",
        color: "#fff",
        fontSize,
        fontWeight: 800,
        letterSpacing: "-0.04em",
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      ABM
    </span>
  );
}

AbmMark.propTypes = { size: PropTypes.number.isRequired };

function WhatsAppMark({ size }) {
  return (
    <Svg size={size} title="WhatsApp">
      <rect width="24" height="24" rx="5" fill="#25D366" />
      <path
        fill="#FFFFFF"
        d="M12.05 4.55A7.35 7.35 0 0 0 5.5 16.45L4.7 19.4l3.05-.8A7.35 7.35 0 1 0 12.05 4.55zm4.25 10.45c-.18.52-1.05.98-1.48 1.02-.4.06-.88.1-2.45-.52-2.4-.98-3.95-3.45-4.08-3.6-.12-.16-.98-1.3-.98-2.48s.62-1.77.84-2c.2-.23.46-.29.62-.29h.46c.15 0 .36-.06.56.43.22.52.72 1.77.78 1.9.06.13.1.28.02.43-.08.16-.12.28-.26.43-.12.15-.27.34-.38.46-.12.13-.25.26-.1.52.15.26.67 1.1 1.44 1.78.98.87 1.8 1.15 2.05 1.28.26.13.42.1.57-.05.16-.16.67-.77.85-.98.17-.2.35-.17.58-.1.24-.07 1.5-.58 2.85-1.98 1.12-1.08 1.9-2.42 2.12-2.83.22-.41.02-.64.16-.84.14-.16.3-.36.46-.54.15-.18.2-.3.31-.5.1-.2.05-.39-.02-.54-.08-.15-.57-1.4-.8-1.9-.2-.47-.43-.4-.57-.4h-.5c-.17 0-.46.07-.7.36-.23.29-.92.9-.92 2.2s.95 2.57 1.08 2.75c.12.17 1.85 2.83 4.5 3.95 1.67.72 2.2.74 2.97.62.48-.08 1.48-.6 1.7-1.18.2-.57.2-1.06.14-1.16-.05-.1-.23-.17-.5-.3z"
      />
    </Svg>
  );
}

WhatsAppMark.propTypes = { size: PropTypes.number.isRequired };

function TelegramMark({ size }) {
  return (
    <Svg size={size} title="Telegram">
      <rect width="24" height="24" rx="5" fill="#26A5E4" />
      <path
        fill="#FFFFFF"
        d="M5.35 11.65 17.1 6.85c.58-.23 1.12.14.93 1l-2.05 9.55c-.14.65-.52.8-1.02.5l-2.82-2.08-1.35 1.3c-.15.15-.28.28-.58.28l.2-2.95 5.4-4.88c.23-.2-.05-.32-.35-.12l-6.7 4.2-2.9-.9c-.63-.2-.65-.62.14-.95z"
      />
    </Svg>
  );
}

TelegramMark.propTypes = { size: PropTypes.number.isRequired };

function GmailMark({ size }) {
  return (
    <Svg size={size} title="Gmail">
      <rect width="24" height="24" rx="5" fill="#FFFFFF" stroke="#DADCE0" strokeWidth="1" />
      <path fill="#EA4335" d="M3.8 6.8 12 13l8.2-6.2V5.5H3.8v1.3z" />
      <path fill="#4285F4" d="M20.2 6.8v10c0 .7-.55 1.25-1.25 1.25H16V10.2l-4 3-4-3V18H5.05c-.7 0-1.25-.55-1.25-1.25v-10L12 13l8.2-6.2z" />
      <path fill="#34A853" d="M3.8 6.8v10c0 .7.55 1.25 1.25 1.25H8V10.2L3.8 6.8z" />
      <path fill="#FBBC04" d="M16 10.2V18h2.95c.7 0 1.25-.55 1.25-1.25v-10L16 10.2z" />
    </Svg>
  );
}

GmailMark.propTypes = { size: PropTypes.number.isRequired };

const MARKS = {
  linkedin: LinkedInMark,
  quora: QuoraMark,
  reddit: RedditMark,
  facebook: FacebookMark,
  instagram: InstagramMark,
  googleads: GoogleAdsMark,
  google_ads: GoogleAdsMark,
  zillow: ZillowMark,
  realtor: RealtorMark,
  redfin: RedfinMark,
  x: XMark,
  twitter: XMark,
  website: WebsiteMark,
  rnc: RncMark,
  custom: AbmMark,
  abm: AbmMark,
  whatsapp: WhatsAppMark,
  telegram: TelegramMark,
  gmail: GmailMark,
  email: GmailMark,
};

export function normalizeChannelKey(key) {
  const k = String(key || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (k === "googleads" || k === "google_ads") return "google_ads";
  if (k === "twitter") return "x";
  if (k === "abm_list" || k === "abmlist") return "custom";
  return k;
}

export function BrandChannelMark({ channelKey, size = 18 }) {
  const key = normalizeChannelKey(channelKey);
  const Mark = MARKS[key];
  if (!Mark) return null;
  return <Mark size={size} />;
}

BrandChannelMark.propTypes = {
  channelKey: PropTypes.string.isRequired,
  size: PropTypes.number,
};

export function hasBrandChannelMark(channelKey) {
  return Boolean(MARKS[normalizeChannelKey(channelKey)]);
}
